import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
// import LandingPage from './pages/LandingPage'; // hidden pre-launch — restore at launch
import ComingSoon from './pages/ComingSoon';
// import FoundingMember from './pages/FoundingMember'; // hidden pre-launch — restore at launch
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Vault from './pages/Vault';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import AssetEditor from './pages/AssetEditor';
import Company from './pages/Company';
import PublicLandingPage from './pages/PublicLandingPage';
import GrowthCenter from './pages/GrowthCenter';
import AdminAIUsage from './pages/AdminAIUsage';
import AdminCampaignReference from './pages/AdminCampaignReference';
import AICoach from './pages/AICoach';
import AdminSupportTickets from './pages/AdminSupportTickets';
import MyBrandPage from './pages/MyBrandPage';
import CheckoutPage from './pages/CheckoutPage';
import TrainingVault from './pages/TrainingVault';
import Automation from './pages/Automation';
import Integrations from './pages/Integrations';
import DemoCampaign from './pages/DemoCampaign';
import StartHere from './pages/StartHere';
import AccountSettings from './pages/AccountSettings';
import Playbooks from './pages/Playbooks';
import CampaignLaunchWizard from './pages/CampaignLaunchWizard';
import AIAdvisor from './pages/AIAdvisor';
import ConnectionsPage from './pages/ConnectionsPage';
import DataDeletion from './pages/DataDeletion';
import { Disclaimer, Terms, Privacy } from './pages/legal/LegalPages';
// import TradeLandingPage from './pages/TradeLandingPage'; // hidden pre-launch — restore at launch
// import FeatureInfoPage from './pages/FeatureInfoPage'; // hidden pre-launch — restore at launch
// import HowItWorksPage from './pages/HowItWorksPage'; // hidden pre-launch — restore at launch
import AuthCallback from './components/GoogleAuth';
import './App.css';

function App() {
  // Synchronous check: process Google auth callback BEFORE any routing/auth checks
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Public marketing pages hidden pre-launch (restore at launch) */}
          <Route path="/public/campaign/:id" element={<PublicLandingPage />} />
          <Route path="/industries/:trade" element={<Navigate to="/" replace />} />
          <Route path="/features/:slug" element={<Navigate to="/" replace />} />
          <Route path="/how-it-works" element={<Navigate to="/" replace />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vault"
            element={
              <ProtectedRoute>
                <Vault />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute>
                <Campaigns />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/campaigns/:trade"
            element={
              <ProtectedRoute>
                <Campaigns />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/campaign/:id/launch"
            element={
              <ProtectedRoute>
                <CampaignLaunchWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/advisor"
            element={
              <ProtectedRoute>
                <AIAdvisor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaign/:id"
            element={
              <ProtectedRoute>
                <CampaignDetail />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/asset/:id"
            element={
              <ProtectedRoute>
                <AssetEditor />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/company"
            element={
              <ProtectedRoute>
                <Company />
              </ProtectedRoute>
            }
          />
          
          {/* Default Routes — Coming Soon gate for unauthenticated visitors */}
          <Route path="/" element={<ComingSoon />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/founding-member" element={<Navigate to="/" replace />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/dashboard" element={<Navigate to="/vault" replace />} />
          
          {/* My Brand */}
          <Route
            path="/my-brand"
            element={
              <ProtectedRoute>
                <MyBrandPage />
              </ProtectedRoute>
            }
          />
          <Route path="/brand" element={<Navigate to="/my-brand" replace />} />
          
          {/* Training Vault */}
          <Route
            path="/training"
            element={
              <ProtectedRoute>
                <TrainingVault />
              </ProtectedRoute>
            }
          />
          
          {/* Growth Center */}
          <Route
            path="/growth"
            element={
              <ProtectedRoute>
                <GrowthCenter />
              </ProtectedRoute>
            }
          />
          
          {/* Marketing Automation */}
          <Route
            path="/automation"
            element={
              <ProtectedRoute>
                <Automation />
              </ProtectedRoute>
            }
          />
          
          {/* Booking Integrations */}
          <Route
            path="/connections"
            element={
              <ProtectedRoute>
                <ConnectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <Integrations />
              </ProtectedRoute>
            }
          />
          
          {/* Demo Campaign (UX prototype, isolated) */}
          <Route
            path="/demo-campaign"
            element={
              <ProtectedRoute>
                <DemoCampaign />
              </ProtectedRoute>
            }
          />
          
          {/* Start Here: Training & Playbook */}
          <Route
            path="/start-here"
            element={
              <ProtectedRoute>
                <StartHere />
              </ProtectedRoute>
            }
          />
          
          {/* Playbooks — educational deep-dive guides only (setup journey lives in Start Here) */}
          <Route
            path="/playbooks"
            element={
              <ProtectedRoute>
                <Playbooks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/playbooks/guides"
            element={
              <ProtectedRoute>
                <Playbooks />
              </ProtectedRoute>
            }
          />
          <Route path="/playbooks/:id" element={<Navigate to="/playbooks" replace />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          
          {/* AI Coach */}
          <Route
            path="/coach"
            element={
              <ProtectedRoute>
                <AICoach />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute>
                <AdminSupportTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/campaign-reference"
            element={
              <ProtectedRoute>
                <AdminCampaignReference />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-usage"
            element={
              <ProtectedRoute>
                <AdminAIUsage />
              </ProtectedRoute>
            }
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
